import type { PaymentProviderType } from './types';

export const SUPPORTED_CURRENCIES = ['ILS', 'USD', 'EUR'] as const;

export type SupportedCurrency = (typeof SUPPORTED_CURRENCIES)[number];

export const CURRENCY_OPTIONS: { label: string; value: SupportedCurrency }[] = [
  { label: 'ILS (₪)', value: 'ILS' },
  { label: 'USD ($)', value: 'USD' },
  { label: 'EUR (€)', value: 'EUR' }
];

export const CURRENCY_SYMBOLS: Record<SupportedCurrency, string> = {
  ILS: '₪',
  USD: '$',
  EUR: '€'
};

export function getCurrencySymbol(currency?: string | null): string {
  const code = String(currency || 'ILS').toUpperCase();
  return isSupportedCurrency(code) ? CURRENCY_SYMBOLS[code] : code;
}

export function isSupportedCurrency(value: unknown): value is SupportedCurrency {
  return typeof value === 'string' && (SUPPORTED_CURRENCIES as readonly string[]).includes(value);
}

/** Currencies the provider type can charge. Grow has no currency API. Nedarim has no EUR. */
export function currenciesCapableForProvider(providerType?: PaymentProviderType | string | null): SupportedCurrency[] {
  switch (providerType) {
    case 'grow':
      return ['ILS'];
    case 'nedarim':
      return ['ILS', 'USD'];
    case 'stripe':
      return ['ILS', 'USD', 'EUR'];
    default:
      return [...SUPPORTED_CURRENCIES];
  }
}

/** Initial allowed/default currencies when creating a provider (or switching type while creating). */
export function defaultCurrencySettingsForProvider(
  providerType?: PaymentProviderType | string | null
): { allowedCurrencies: SupportedCurrency[]; defaultCurrency: SupportedCurrency } {
  if (providerType === 'stripe') {
    const allowedCurrencies = currenciesCapableForProvider('stripe');
    return {
      allowedCurrencies,
      defaultCurrency: normalizeDefaultCurrency('USD', allowedCurrencies, 'USD')
    };
  }
  return normalizeCurrencySettings(undefined, undefined, providerType);
}

export function normalizeAllowedCurrencies(
  raw: unknown,
  providerType?: PaymentProviderType | string | null
): SupportedCurrency[] {
  const capable = currenciesCapableForProvider(providerType);
  const capableSet = new Set(capable);
  const fromRaw = Array.isArray(raw)
    ? raw.filter((c): c is SupportedCurrency => isSupportedCurrency(c) && capableSet.has(c))
    : [];
  const unique = [...new Set(fromRaw)];
  return unique.length > 0 ? unique : capable.includes('ILS') ? ['ILS'] : [capable[0]];
}

export function normalizeDefaultCurrency(
  raw: unknown,
  allowed: SupportedCurrency[],
  fallback: SupportedCurrency = 'ILS'
): SupportedCurrency {
  if (isSupportedCurrency(raw) && allowed.includes(raw)) return raw;
  if (allowed.includes(fallback)) return fallback;
  return allowed[0] ?? 'ILS';
}

export function normalizeCurrencySettings(
  allowedRaw: unknown,
  defaultRaw: unknown,
  providerType?: PaymentProviderType | string | null
): { allowedCurrencies: SupportedCurrency[]; defaultCurrency: SupportedCurrency } {
  const allowedCurrencies = normalizeAllowedCurrencies(allowedRaw, providerType);
  const defaultCurrency = normalizeDefaultCurrency(defaultRaw, allowedCurrencies);
  return { allowedCurrencies, defaultCurrency };
}

export function formatMoneyAmount(value: number | string | null | undefined, currency = 'ILS'): string {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (isNaN(n)) return String(value);
  const currencyCode = String(currency || 'ILS').toUpperCase();
  const symbol = getCurrencySymbol(currencyCode);
  return `${symbol}${n.toFixed(2)}`;
}

/**
 * Stripe card-charge minima we enforce in product (major units).
 * Docs list 0.50 for ILS/USD/EUR when settlement matches the charge currency.
 * ILS uses a higher floor so converted ILS→USD settlement still clears ~$0.50.
 * @see https://docs.stripe.com/currencies#minimum-and-maximum-charge-amounts
 */
export const STRIPE_MIN_AMOUNTS: Record<SupportedCurrency, number> = {
  ILS: 3,
  USD: 0.5,
  EUR: 0.5
};

export function getStripeMinAmount(currency?: string | null): number {
  const code = String(currency || 'USD').toUpperCase();
  return isSupportedCurrency(code) ? STRIPE_MIN_AMOUNTS[code] : STRIPE_MIN_AMOUNTS.USD;
}

export function meetsStripeMinimum(amount: number, currency?: string | null): boolean {
  return Number.isFinite(amount) && amount >= getStripeMinAmount(currency);
}

export function stripeMinimumAmountMessage(currency?: string | null): string {
  const min = getStripeMinAmount(currency);
  const symbol = getCurrencySymbol(currency);
  return `סכום מינימלי לתשלום ב-Stripe: ${symbol}${min % 1 === 0 ? min.toFixed(0) : min.toFixed(2)}`;
}

/** Parse SYSTEM_ALLOWED_CURRENCIES env (comma-separated) + SYSTEM_DEFAULT_CURRENCY. */
export function parseSystemCurrencyEnv(
  allowedEnv?: string | null,
  defaultEnv?: string | null,
  providerType?: PaymentProviderType | string | null
): { allowedCurrencies: SupportedCurrency[]; defaultCurrency: SupportedCurrency } {
  const rawAllowed = allowedEnv
    ? allowedEnv
        .split(',')
        .map(s => s.trim().toUpperCase())
        .filter(Boolean)
    : undefined;
  return normalizeCurrencySettings(rawAllowed, defaultEnv?.trim().toUpperCase(), providerType);
}
